import { useState, useMemo } from 'react'
import { useForm, Link } from '@inertiajs/react'
import { Layout } from '@/components/Layout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { TimezoneSelect } from '@/components/TimezoneSelect'
import { ArrowLeft, Check, ChevronsUpDown, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

interface Client {
  id: string
  name: string
}

interface Props {
  instance: {
    client_id: string | null
    name: string
    base_url: string
    api_key: string
    timezone: string
  }
  clients: Client[]
  errors: Record<string, string[]>
}

const CREATE_NEW_CLIENT_VALUE = '__create_new__'

export default function N8nInstanceNew({ instance, clients, errors }: Props) {
  const [clientPopoverOpen, setClientPopoverOpen] = useState(false)
  const [isCreatingNewClient, setIsCreatingNewClient] = useState(false)

  const { data, setData, post, processing } = useForm({
    n8n_instance: {
      client_id: instance.client_id || '',
      name: instance.name,
      base_url: instance.base_url,
      api_key: instance.api_key,
      timezone: instance.timezone,
    },
    new_client: {
      name: '',
      code: '',
    }
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post('/n8n_instances')
  }

  const formData = data.n8n_instance
  const newClientData = data.new_client

  const setFormData = (key: keyof typeof formData, value: string) => {
    setData('n8n_instance', { ...formData, [key]: value })
  }

  const setNewClientData = (key: keyof typeof newClientData, value: string) => {
    setData('new_client', { ...newClientData, [key]: value })
  }

  const handleClientSelect = (value: string) => {
    if (value === CREATE_NEW_CLIENT_VALUE) {
      setIsCreatingNewClient(true)
      setFormData('client_id', '')
    } else {
      setIsCreatingNewClient(false)
      setFormData('client_id', value)
      // Clear new client fields when selecting existing client
      setData('new_client', { name: '', code: '' })
    }
    setClientPopoverOpen(false)
  }

  const selectedClient = useMemo(() => {
    if (isCreatingNewClient) return null
    return clients.find((c) => c.id === formData.client_id)
  }, [clients, formData.client_id, isCreatingNewClient])

  // Auto-generate code from client name
  const handleNewClientNameChange = (name: string) => {
    setNewClientData('name', name)
    // Auto-generate code: lowercase, replace spaces with underscores, remove special chars
    const generatedCode = name
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '_')
      .substring(0, 20)
    setNewClientData('code', generatedCode)
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link href="/n8n_instances">
              <ArrowLeft className="h-4 w-4" />
              <span className="sr-only">Back</span>
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">New n8n Instance</h1>
            <p className="text-muted-foreground">
              Add a new n8n deployment to monitor.
            </p>
          </div>
        </div>

        <Card className="max-w-2xl">
          <CardHeader>
            <CardTitle>Instance Details</CardTitle>
            <CardDescription>
              Enter the connection details for the n8n instance.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Client</Label>
                <Popover open={clientPopoverOpen} onOpenChange={setClientPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={clientPopoverOpen}
                      className="w-full justify-between font-normal"
                    >
                      {isCreatingNewClient ? (
                        <span className="flex items-center gap-2">
                          <Plus className="h-4 w-4" />
                          Creating new client...
                        </span>
                      ) : selectedClient ? (
                        selectedClient.name
                      ) : (
                        <span className="text-muted-foreground">Select a client</span>
                      )}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search clients..." />
                      <CommandList>
                        <CommandEmpty>No client found.</CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            value={CREATE_NEW_CLIENT_VALUE}
                            onSelect={handleClientSelect}
                            className="text-primary"
                          >
                            <Plus className="mr-2 h-4 w-4" />
                            Create new client
                          </CommandItem>
                        </CommandGroup>
                        <CommandGroup heading="Existing Clients">
                          {clients.map((client) => (
                            <CommandItem
                              key={client.id}
                              value={client.id}
                              onSelect={handleClientSelect}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  formData.client_id === client.id && !isCreatingNewClient
                                    ? "opacity-100"
                                    : "opacity-0"
                                )}
                              />
                              {client.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {errors.client_id && (
                  <p className="text-sm text-destructive">{errors.client_id[0]}</p>
                )}
                {errors.client && (
                  <p className="text-sm text-destructive">{errors.client[0]}</p>
                )}
              </div>

              {/* Inline new client fields */}
              {isCreatingNewClient && (
                <div className="rounded-lg border bg-muted/50 p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium">New Client Details</h4>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setIsCreatingNewClient(false)
                        setData('new_client', { name: '', code: '' })
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new_client_name">Client Name</Label>
                    <Input
                      id="new_client_name"
                      value={newClientData.name}
                      onChange={(e) => handleNewClientNameChange(e.target.value)}
                      placeholder="Acme Corp"
                    />
                    {errors['new_client.name'] && (
                      <p className="text-sm text-destructive">{errors['new_client.name'][0]}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new_client_code">Client Code</Label>
                    <Input
                      id="new_client_code"
                      value={newClientData.code}
                      onChange={(e) => setNewClientData('code', e.target.value)}
                      placeholder="acme_corp"
                    />
                    <p className="text-xs text-muted-foreground">
                      Short unique identifier (auto-generated from name)
                    </p>
                    {errors['new_client.code'] && (
                      <p className="text-sm text-destructive">{errors['new_client.code'][0]}</p>
                    )}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="name">Instance Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData('name', e.target.value)}
                  placeholder="Production"
                />
                {errors.name && (
                  <p className="text-sm text-destructive">{errors.name[0]}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="base_url">Base URL</Label>
                <Input
                  id="base_url"
                  value={formData.base_url}
                  onChange={(e) => setFormData('base_url', e.target.value)}
                  placeholder="https://n8n.example.com"
                />
                <p className="text-sm text-muted-foreground">
                  The base URL of the n8n instance (without trailing slash).
                </p>
                {errors.base_url && (
                  <p className="text-sm text-destructive">{errors.base_url[0]}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="api_key">API Key</Label>
                <Input
                  id="api_key"
                  type="password"
                  value={formData.api_key}
                  onChange={(e) => setFormData('api_key', e.target.value)}
                  placeholder="n8n_api_..."
                />
                <p className="text-sm text-muted-foreground">
                  Generate an API key in your n8n instance settings.
                </p>
                {errors.api_key && (
                  <p className="text-sm text-destructive">{errors.api_key[0]}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Timezone (optional)</Label>
                <TimezoneSelect
                  value={formData.timezone}
                  onValueChange={(value) => setFormData('timezone', value)}
                  placeholder="Select timezone..."
                />
                {errors.timezone && (
                  <p className="text-sm text-destructive">{errors.timezone[0]}</p>
                )}
              </div>

              {errors.base && (
                <p className="text-sm text-destructive">{errors.base[0]}</p>
              )}

              <div className="flex gap-4">
                <Button type="submit" disabled={processing}>
                  {processing ? 'Creating...' : 'Create Instance'}
                </Button>
                <Button variant="outline" asChild>
                  <Link href="/n8n_instances">Cancel</Link>
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  )
}
